import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { InputAdornment, TextField } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { SellerConfirmationDialog } from "./Sales/SellerConfirmationDialog";
import { SuccessOrderDialog } from "./Sales/SuccessOrderDialog";
import { UserContext } from "../../hooks/userContext";
import { useQueryBankAccount } from "../../queries/pharmacy/hooks/useQueryBankAccount";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import { routeConfigs } from "../../navigation/routeConfig";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { pageTitleSx } from "../../constants/designTokens";
import {
  getHistoryGridColumns,
  historyDataGridSx,
  HistoryGridRow,
  mapOrdersToHistoryGridRows,
} from "../historyGrid";
import { BrandLoaderOverlay } from "../../components/shared/BrandLoader/BrandLoader";

export const OpenOrders: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const [open, setOpen] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState<null | number>(null);
  const [orderNumber, setOrderNumber] = useState<any>(0);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [refetchState, setRefetchState] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const navigate = useNavigate();

  const refetch = () => {
    setRefetchState((prev) => !prev);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const api = `${process.env.REACT_APP_API_URL}/v2/order/get/openOrdersSeller?userId=${id}`;
    axios
      .get(api)
      .then((response) => {
        setData(response.data.data ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        addToast({ type: NotificationType.Error, title: "Failed to load sell orders." });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, refetchState, addToast]);

  const { data: bankAccount } = useQueryBankAccount({
    variables: {
      where: {
        AND: [{ object: { equals: "bank_account" } }, { userId: { equals: +id } }, { accountId: { not: { equals: null } } }],
      },
    },
    fetchPolicy: "no-cache",
  });

  const handleConfirm = useCallback(
    (item: HistoryGridRow) => {
      if (bankAccount?.findFirstBankAccount) {
        setOrderNumber(item.orderNumber);
        setOpen(true);
      } else {
        addToast({
          type: NotificationType.Error,
          title: "You are required to add your bank information before you can confirm order.",
        });
        navigate(`${routeConfigs.bankAccount.path}?section=payout`);
      }
    },
    [bankAccount, addToast, navigate]
  );

  const rows = useMemo(() => {
    const mapped = mapOrdersToHistoryGridRows(data, "seller").map((row) => ({
      ...row,
      // Open orders show summed line totals (pre-confirmation), not payout breakdown
      totalLabel: row.orders
        .reduce((sum, order) => sum + Number(order.totalAmount || 0), 0)
        .toFixed(2),
    }));

    const query = searchInput.trim().toLowerCase();
    if (!query) return mapped;

    return mapped.filter(
      (row) =>
        String(row.orderNumber).toLowerCase().includes(query) ||
        String(row.counterpartyName || "").toLowerCase().includes(query)
    );
  }, [data, searchInput]);

  const columns = useMemo(() => {
    const base = getHistoryGridColumns({
      mode: "seller",
      onCopy: (value) => {
        navigator.clipboard.writeText(value);
        addToast({ type: NotificationType.Success, title: "Copied to Clipboard" });
      },
    }).filter((col) => col.field !== "actions" && col.field !== "trackingNumber");

    const confirmColumn: GridColDef = {
      field: "actions",
      headerName: "Actions",
      flex: 1.1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box onClick={(event) => event.stopPropagation()}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleConfirm(params.row as HistoryGridRow)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              fontSize: 12,
              py: 0.25,
              px: 1.5,
              minWidth: 0,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Confirm Order
          </Button>
        </Box>
      ),
    };

    return [...base, confirmColumn];
  }, [addToast, handleConfirm]);

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "14px",
          boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontFamily: '"Source Sans 3", sans-serif',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 0.75,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E2E8F0",
            minHeight: 44,
          }}
        >
          <Typography component="h1" sx={pageTitleSx}>
            Sell Orders
          </Typography>
          <TextField
            type="text"
            placeholder="Search by Buyer or Order Number…"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPaginationModel((model) => ({ ...model, page: 0 }));
            }}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: '"Source Sans 3", sans-serif',
                fontSize: 14,
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#475569" },
                "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "1.5px" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: "#475569" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
          <DataGrid
            loading={loading}
            rows={rows}
            columns={columns}
            rowHeight={48}
            columnHeaderHeight={40}
            autoPageSize
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[paginationModel.pageSize]}
            disableRowSelectionOnClick
            sx={{
              ...historyDataGridSx,
              height: "100%",
              [`& .MuiDataGrid-row:hover`]: {
                cursor: "default",
                backgroundColor: "#F1F5F9",
              },
            }}
            localeText={{ noRowsLabel: "No new sell orders" }}
            getRowId={(row) => row.id}
            slots={{ loadingOverlay: BrandLoaderOverlay }}
          />
        </Box>
      </Box>

      {open && (
        <SellerConfirmationDialog
          open={open}
          setOpen={setOpen}
          data={data}
          orderNumber={orderNumber}
          setSuccessOrderId={setSuccessOrderId}
          refetch={refetch}
        />
      )}
      {successOrderId !== null && (
        <SuccessOrderDialog setSuccessOrderId={setSuccessOrderId} successOrderId={successOrderId} refetch={refetch} />
      )}
    </Box>
  );
};

export default OpenOrders;
